import type { Brand } from "../types";

export interface BrandListItemProps {
	brand: Brand;
	onSwitch?: (brandId: string) => void;
	isSelected?: boolean;
}

function getRoleBadgeStyles(role: string): { text: string; dot: string } {
	switch (role) {
		case "admin":
			return { text: "text-green-700", dot: "bg-green-500" };
		case "member":
			return { text: "text-blue-700", dot: "bg-blue-500" };
		case "viewer":
			return { text: "text-gray-600", dot: "bg-gray-400" };
		default:
			return { text: "text-gray-600", dot: "bg-gray-400" };
	}
}

function formatRole(role: string): string {
	return role.charAt(0).toUpperCase() + role.slice(1);
}

export default function BrandListItem({ brand, onSwitch, isSelected }: BrandListItemProps) {
	const { id, logo_url, name, website_url, role } = brand;
	const roleStyles = getRoleBadgeStyles(role);

	return (
		<button
			type="button"
			onClick={() => onSwitch?.(id)}
			className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all text-left cursor-pointer ${
				isSelected ? "border-gray-300 bg-gray-50" : "bg-white border-gray-200 hover:border-gray-300"
			}`}
		>
			{/* Left: Logo and metadata */}
			<div className="flex items-center gap-4 min-w-0 flex-1">
				{/* Brand logo/avatar */}
				{logo_url ? (
					<img src={logo_url} alt={name} className="w-10 h-10 rounded-lg object-cover shrink-0" />
				) : (
					<div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center shrink-0">
						<span className="text-sm font-semibold text-brand">{name.charAt(0).toUpperCase()}</span>
					</div>
				)}

				{/* Name and website */}
				<div className="min-w-0">
					<h3 className="text-sm font-medium text-primary truncate">{name}</h3>
					{website_url && (
						<div className="flex items-center gap-3 mt-1">
							<span className="text-xs text-tertiary truncate">{website_url}</span>
						</div>
					)}
				</div>
			</div>

			{/* Right: Role badge */}
			<div className="flex items-center gap-2 shrink-0">
				<span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md ${roleStyles.text}`}>
					<span className={`w-1.5 h-1.5 rounded-full ${roleStyles.dot}`} />
					{formatRole(role)}
				</span>
			</div>
		</button>
	);
}
